// Lightweight, dependency-free SVG charts for the redesign (PDF Notes 10).
// Reference-grade donut / line / bar, themed to the design tokens. Pure string
// output — drop the result into any innerHTML. Colors accept CSS vars.

export interface Seg { label: string; value: number; color: string }

const NAVY = '#312e81';

// Donut with a colored ring + a center total + a side legend.
export function donut(segs: Seg[], opts: { size?: number; centerLabel?: string; centerValue?: string } = {}): string {
  const size = opts.size || 150;
  const r = size / 2 - 10;
  const cx = size / 2, cy = size / 2;
  const stroke = 16;
  const total = segs.reduce((s, x) => s + (x.value || 0), 0) || 1;
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const arcs = segs.filter(s => s.value > 0).map(s => {
    const frac = s.value / total;
    const dash = `${frac * circ} ${circ - frac * circ}`;
    const el = `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${s.color}" stroke-width="${stroke}" stroke-dasharray="${dash}" stroke-dashoffset="${-offset}" transform="rotate(-90 ${cx} ${cy})"></circle>`;
    offset += frac * circ;
    return el;
  }).join('');
  const center = (opts.centerValue || opts.centerLabel)
    ? `<text x="${cx}" y="${cy - 2}" text-anchor="middle" style="font:600 18px var(--font-mono); fill:var(--text);">${opts.centerValue || ''}</text>
       <text x="${cx}" y="${cy + 14}" text-anchor="middle" style="font:600 9px var(--font-sans); fill:var(--text-muted); text-transform:uppercase; letter-spacing:.05em;">${opts.centerLabel || ''}</text>`
    : '';
  const legend = segs.map(s => `
    <div style="display:flex; align-items:center; gap:7px; font-size:12px; color:var(--text-muted);">
      <span style="width:10px; height:10px; border-radius:3px; background:${s.color}; flex:none;"></span>
      <span style="color:var(--text);">${s.label}</span>
      <span style="margin-left:auto; font-variant-numeric:tabular-nums; color:var(--text);">${s.value}</span>
    </div>`).join('');
  return `<div style="display:flex; align-items:center; gap:18px; flex-wrap:wrap;">
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" style="flex:none;">
      <circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="var(--border-soft)" stroke-width="${stroke}"></circle>
      ${arcs}${center}
    </svg>
    <div style="display:flex; flex-direction:column; gap:8px; min-width:140px; flex:1;">${legend}</div>
  </div>`;
}

// Line chart with a soft gradient fill, gridlines, dots, axis labels.
export function lineChart(points: { label: string; value: number }[], opts: { width?: number; height?: number; fmt?: (n: number) => string; color?: string } = {}): string {
  const w = opts.width || 560, h = opts.height || 230;
  const padL = 46, padR = 14, padT = 14, padB = 26;
  const iw = w - padL - padR, ih = h - padT - padB;
  const fmt = opts.fmt || ((n) => String(Math.round(n)));
  const color = opts.color || NAVY;
  if (!points.length) return `<div style="height:${h}px; display:flex; align-items:center; justify-content:center; color:var(--text-faint); font-size:12px;">No data</div>`;
  const vals = points.map(p => p.value);
  const max = Math.max(...vals, 1), min = Math.min(...vals, 0);
  const span = (max - min) || 1;
  const x = (i: number) => padL + (points.length === 1 ? iw / 2 : (i / (points.length - 1)) * iw);
  const y = (v: number) => padT + ih - ((v - min) / span) * ih;
  const pts = points.map((p, i) => `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const area = `${padL},${padT + ih} ${pts} ${(padL + iw).toFixed(1)},${padT + ih}`;
  const ticks = 4;
  const grid = Array.from({ length: ticks + 1 }, (_, k) => {
    const v = min + (span * k) / ticks; const yy = y(v);
    return `<line x1="${padL}" y1="${yy.toFixed(1)}" x2="${padL + iw}" y2="${yy.toFixed(1)}" stroke="var(--border-soft)" stroke-width="1"></line>
            <text x="${padL - 8}" y="${(yy + 3).toFixed(1)}" text-anchor="end" style="font:11px var(--font-mono); fill:var(--text-faint);">${fmt(v)}</text>`;
  }).join('');
  const dots = points.map((p, i) => `<circle cx="${x(i).toFixed(1)}" cy="${y(p.value).toFixed(1)}" r="3" fill="${color}"></circle>`).join('');
  const xlabels = points.map((p, i) => (points.length <= 14 || i % 2 === 0)
    ? `<text x="${x(i).toFixed(1)}" y="${h - 8}" text-anchor="middle" style="font:10.5px var(--font-sans); fill:var(--text-muted);">${p.label}</text>` : '').join('');
  return `<svg width="100%" viewBox="0 0 ${w} ${h}" preserveAspectRatio="none" style="display:block;">
    <defs><linearGradient id="lcfill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="${color}" stop-opacity="0.14"/><stop offset="100%" stop-color="${color}" stop-opacity="0"/></linearGradient></defs>
    ${grid}
    <polygon points="${area}" fill="url(#lcfill)"></polygon>
    <polyline points="${pts}" fill="none" stroke="${color}" stroke-width="2.5" stroke-linejoin="round" stroke-linecap="round"></polyline>
    ${dots}${xlabels}
  </svg>`;
}

// Simple vertical bar chart.
export function barChart(bars: Seg[], opts: { height?: number; fmt?: (n: number) => string } = {}): string {
  const h = opts.height || 180;
  const fmt = opts.fmt || ((n) => String(n));
  const max = Math.max(...bars.map(b => b.value), 1);
  return `<div style="display:flex; align-items:flex-end; gap:14px; height:${h}px; padding-top:8px;">
    ${bars.map(b => `
      <div style="flex:1; display:flex; flex-direction:column; align-items:center; gap:6px; height:100%; justify-content:flex-end;">
        <span style="font:600 12px var(--font-mono); color:var(--text);">${fmt(b.value)}</span>
        <div style="width:100%; max-width:54px; height:${Math.max(2, (b.value / max) * (h - 44))}px; background:${b.color}; border-radius:6px 6px 0 0;"></div>
        <span style="font-size:10.5px; color:var(--text-muted); text-align:center;">${b.label}</span>
      </div>`).join('')}
  </div>`;
}
